import { ArrowRight, Shield, CheckCircle, TrendingUp, Percent, Clock, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import SEO from '../SEO';
import { pageSEO, faqSchemas } from '../../seoData';
import RelatedServices from '../RelatedServices';

const FixedDeposits = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqs = [
    { question: "Why choose NBFC FDs over bank FDs?", answer: "NBFC Fixed Deposits typically offer 1-2% higher interest rates compared to bank FDs. We only recommend FDs from AAA/AA+ rated NBFCs like Bajaj Finance, Mahindra Finance etc., which are as safe as bank deposits. Higher returns with similar safety makes them attractive." },
    { question: "Are NBFC Fixed Deposits safe?", answer: "NBFC FDs from highly rated companies (AAA/AA+) are considered very safe. However, they are not covered under DICGC insurance (which covers bank FDs up to Rs.5 lakhs). We mitigate this risk by only recommending top-rated, financially strong NBFCs with proven track records." },
    { question: "What are the interest payout options?", answer: "NBFC FDs offer multiple payout options: Monthly interest payout for regular income, Quarterly payouts, Annual payouts, or Cumulative option where interest is compounded and paid at maturity. Choose based on whether you need regular income or want maximum compounding." },
    { question: "What is the minimum FD amount?", answer: "Minimum investment varies by NBFC - typically Rs.15,000 to Rs.25,000. Tenures range from 12 months to 60 months. We help you compare rates, tenures, and ratings across multiple NBFCs to find the best option for your needs." },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO {...pageSEO.fixedDeposits} faqJsonLd={faqSchemas.fixedDeposits} />

      <section className="relative bg-gradient-to-br from-cyan-50 via-white to-sky-50 pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <span className="inline-block bg-cyan-600 text-white px-4 py-2 rounded-full text-sm font-semibold tracking-wide mb-6">
              HIGH-RETURN FIXED DEPOSITS
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              NBFC Fixed Deposits
              <span className="block bg-gradient-to-r from-cyan-600 to-sky-600 bg-clip-text text-transparent">
                Higher Returns, Trusted Safety
              </span>
            </h1>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-3xl">
              Get 1-2% higher returns compared to bank FDs with NBFC Fixed Deposits from top-rated 
              companies. We exclusively recommend AAA/AA+ rated NBFCs ensuring your capital safety 
              while maximizing returns. Multiple tenure and payout options to match your income needs 
              and investment goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="bg-gradient-to-r from-cyan-600 to-sky-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-cyan-700 hover:to-sky-700 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-xl" data-testid="fd-cta">
                Compare FD Rates <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link to="/services" className="border-2 border-cyan-600 text-cyan-600 hover:bg-cyan-600 hover:text-white px-8 py-4 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center">
                View All Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div><div className="text-3xl font-bold text-cyan-600 mb-1">AAA/AA+</div><div className="text-gray-600 text-sm">Rated NBFCs Only</div></div>
            <div><div className="text-3xl font-bold text-cyan-600 mb-1">1-2%</div><div className="text-gray-600 text-sm">Higher Than Bank FDs</div></div>
            <div><div className="text-3xl font-bold text-cyan-600 mb-1">Rs.15K</div><div className="text-gray-600 text-sm">Min Investment</div></div>
            <div><div className="text-3xl font-bold text-cyan-600 mb-1">Flexible</div><div className="text-gray-600 text-sm">Payout Options</div></div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12 text-center">Why Choose NBFC Fixed Deposits?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: <Percent className="h-7 w-7 text-white" />, title: "Higher Interest Rates", desc: "NBFC FDs offer 1-2% higher interest compared to leading bank FDs. Over 3-5 years, this difference compounds to significantly more returns on your investment." },
              { icon: <Shield className="h-7 w-7 text-white" />, title: "Top-Rated Safety", desc: "We only recommend FDs from AAA/AA+ rated NBFCs with proven financial stability. Companies like Bajaj Finance, Mahindra Finance have excellent track records of safety." },
              { icon: <TrendingUp className="h-7 w-7 text-white" />, title: "Multiple Payout Options", desc: "Choose monthly, quarterly, annual, or cumulative interest payouts. Monthly payouts are ideal for retirees while cumulative options maximize compounding for wealth builders." },
              { icon: <Clock className="h-7 w-7 text-white" />, title: "Flexible Tenures", desc: "Choose from 12 to 60 months tenure. Ladder your FDs across different tenures for optimal liquidity management while earning attractive returns." },
              { icon: <Building2 className="h-7 w-7 text-white" />, title: "Trusted Companies", desc: "Invest with India's most trusted NBFCs. Each recommendation is backed by our thorough analysis of the company's financial health, credit rating, and track record." },
              { icon: <CheckCircle className="h-7 w-7 text-white" />, title: "Easy Process", desc: "We handle the entire FD application process. Simple documentation, online or offline application, and regular maturity tracking. Completely hassle-free investing." },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-sky-600 rounded-2xl flex items-center justify-center mb-5">{item.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-10 text-center">NBFC Fixed Deposit FAQs</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-gray-50 rounded-xl overflow-hidden">
                <button onClick={() => setExpandedFaq(expandedFaq === i ? null : i)} className="w-full px-6 py-5 text-left flex justify-between items-center">
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <span className="text-2xl text-cyan-600 ml-4">{expandedFaq === i ? '−' : '+'}</span>
                </button>
                {expandedFaq === i && <div className="px-6 pb-5"><p className="text-gray-700 leading-relaxed">{faq.answer}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <RelatedServices links={['bonds', 'gold', 'retirement', 'nri', 'calculator']} />

      <section className="py-16 bg-gradient-to-r from-cyan-600 to-sky-600">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Earn More on Your Fixed Deposits</h2>
          <p className="text-lg mb-8 text-cyan-100">Compare NBFC FD rates and find the best option for your investment. Free guidance.</p>
          <Link to="/contact" className="bg-white text-cyan-700 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all inline-flex items-center">
            Compare FD Rates <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default FixedDeposits;
